import { motion } from 'framer-motion';
import { Star, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import type { LibraryFilter, LibraryItem } from '@shared/types';

import { PerspectiveCover } from '@/components/PerspectiveCover';
import { Poster } from '@/components/Poster';
import { Reveal } from '@/components/Reveal';
import { StatusOverlay } from '@/components/StatusOverlay';
import { Skeleton } from '@/components/ui/skeleton';
import { localized } from '@/i18n/localized';
import { useT, type Locale } from '@/i18n/locale';
import { libraryItemLabel, STATUS_NOUN_KEY } from '@/lib/library';
import { staggerChild, staggerParent } from '@/lib/motion';
import { useMediaQuery } from '@/lib/use-media-query';
import { useTitlePrefetch } from '@/features/show/api';
import { titlePath } from '@/lib/title-path';

import { CoverQuickActions } from './CoverQuickActions';
import { hiddenStatusMarks } from './filters';

/** Hover quick actions only on a mouse/trackpad — never on touch (screens.md §`/library`). */
export const QUICK_ACTIONS_QUERY = '(hover: hover) and (pointer: fine)';

/**
 * The shelf's tracks: 3 across a phone, ~7 at 1440 beside the nav rail.
 * Shared with the loading skeleton so nothing jumps when data lands.
 */
const SHELF_GRID =
  'm-0 grid list-none grid-cols-[repeat(auto-fill,minmax(100px,1fr))] gap-x-3 gap-y-5 p-0 sm:grid-cols-[repeat(auto-fill,minmax(130px,1fr))] md:grid-cols-[repeat(auto-fill,minmax(150px,1fr))] md:gap-x-4 md:gap-y-6';
/** The rendered cover width per tier (the `<img sizes>` hint). */
const SHELF_SIZES = '(min-width: 768px) 200px, (min-width: 640px) 170px, 34vw';

/**
 * Covers that load eagerly with `fetchpriority="high"` — about one row at the
 * widest layout (ui-ux.md §7 Performance budgets).
 * ponytail: a fixed count, not the measured column count; 2–3 rows on a phone.
 */
const PRIORITY_COVERS = 8;
/** Covers that take part in the load settle (two rows at 1440); the rest render at rest. */
const SETTLE_COVERS = 14;

/**
 * One item of the collection (a cover tile or a row). `settle` = the view's
 * first collection: it rises with the load settle (`Reveal`, `order` past
 * `SETTLE.maxOrder` renders at rest). After a lens change the re-keyed
 * collection replays the quick catalog stagger instead (ui-ux.md §4) — a
 * filter tap is an interaction, not a page load.
 */
export function ShelfItem({
  settle,
  order,
  className,
  children,
}: {
  settle: boolean;
  order: number;
  className?: string;
  children: ReactNode;
}) {
  return settle ? (
    <Reveal as="li" order={order} className={className}>
      {children}
    </Reveal>
  ) : (
    <motion.li variants={staggerChild()} className={className}>
      {children}
    </motion.li>
  );
}

/** The collection's `<ul>`: carries the catalog stagger for `ShelfItem`s that don't settle. */
export function ShelfList({ count, className, children }: { count: number; className: string; children: ReactNode }) {
  return (
    <motion.ul variants={staggerParent(count)} initial="initial" animate="animate" className={className}>
      {children}
    </motion.ul>
  );
}

/**
 * The Library cover wall — the shelf: denser than `PosterGrid` (100 → 130 →
 * 150px tracks), each cover a `PerspectiveCover variant="lift"` (a gentle
 * turn on hover/focus, nothing at rest) that keeps the `poster-${id}` morph
 * into the title page, plus the hover quick-action bar. Under each cover the
 * name and one quiet meta line (`ShelfMeta`). Covers stay clean: no
 * Completed seal, no mark the active filter already implies
 * (`hiddenStatusMarks`), and a calm Dropped mark. The cover link is an
 * overlay *inside* the poster and the bar is its sibling, so the bar is
 * never nested in a link and clicking it never navigates. Tab order per
 * tile: cover link → rating → status.
 */
export function LibraryGrid({
  items,
  locale,
  filter,
  settle,
}: {
  items: LibraryItem[];
  locale: Locale;
  filter: LibraryFilter;
  settle: boolean;
}) {
  const quickActions = useMediaQuery(QUICK_ACTIONS_QUERY);
  const omit = hiddenStatusMarks(filter);
  return (
    <ShelfList count={items.length} className={SHELF_GRID}>
      {items.map((item, i) => (
        <ShelfItem
          key={item.title.id}
          settle={settle}
          // Two covers per 110ms step: 14 covers inside the settle's 10 steps.
          order={i < SETTLE_COVERS ? Math.floor(i / 2) : Number.POSITIVE_INFINITY}
          className="group/tile min-w-0"
        >
          <LibraryTile
            item={item}
            locale={locale}
            quickActions={quickActions}
            omit={omit}
            priority={i < PRIORITY_COVERS}
          />
        </ShelfItem>
      ))}
    </ShelfList>
  );
}

function LibraryTile({
  item,
  locale,
  quickActions,
  omit,
  priority,
}: {
  item: LibraryItem;
  locale: Locale;
  quickActions: boolean;
  omit: ReturnType<typeof hiddenStatusMarks>;
  priority: boolean;
}) {
  const id = item.title.id;
  const prefetch = useTitlePrefetch(id, item.title.type);
  const name = localized(item.title.title, item.title.titleBr, locale);
  const dropped = item.status === 'dropped' && !omit.includes('dropped');
  return (
    <>
      <PerspectiveCover variant="lift">
        <Poster
          id={id}
          name={name}
          palette={item.title.palette}
          imageUrl={item.title.posterUrl}
          imageFallbackUrl={item.title.posterFallbackUrl}
          sizes={SHELF_SIZES}
          priority={priority}
          lift={false}
        >
          {/* Dropped is drawn here (calm), not by the shared overlay's red disc. */}
          <StatusOverlay item={item} rating={false} omit={[...omit, 'dropped']} />
          {dropped ? <DroppedMark /> : null}
          {/* Inset focus ring: the poster clips overflow, so the app-wide 2px
              offset would be cut off. */}
          <Link
            to={titlePath(id, item.title.type)}
            {...prefetch}
            aria-label={libraryItemLabel(item, locale)}
            className="absolute inset-0 rounded-sm focus-visible:-outline-offset-2"
          />
          {quickActions ? <CoverQuickActions item={item} name={name} /> : null}
        </Poster>
      </PerspectiveCover>
      {/* The name opens the title too; the cover link above is the one named
          tab stop (its label carries the status and rating), so this
          duplicate stays out of the a11y tree. */}
      <Link to={titlePath(id, item.title.type)} tabIndex={-1} aria-hidden className="mt-2 block">
        <span className="block truncate text-caption font-semibold text-ink">{name}</span>
        <ShelfMeta item={item} omit={omit} />
      </Link>
    </>
  );
}

/**
 * The quiet line under a cover: what the cover doesn't already say. The
 * status word for a title still on its way (Watchlist, Dropped — unless the
 * filter implies it), episode progress for Watching, and your ★ when rated.
 * A finished, unrated title has an empty line (its height is kept, so the
 * rows stay level).
 */
function ShelfMeta({ item, omit }: { item: LibraryItem; omit: ReturnType<typeof hiddenStatusMarks> }) {
  const t = useT();
  const { status } = item;
  let state: string | null = null;
  if (status === 'watching' && item.totalEpisodes > 0) {
    state = t('upnext.progressOf', { watched: item.watchedCount, total: item.totalEpisodes });
  } else if (status !== 'removed' && status !== 'completed' && !omit.includes(status)) {
    state = t(STATUS_NOUN_KEY[status]);
  }
  return (
    <span className="mt-0.5 flex min-h-5 items-center gap-2 text-caption text-ink-tertiary">
      {state ? <span className="truncate tabular-nums">{state}</span> : null}
      {item.userRating ? <UserRating value={item.userRating} /> : null}
    </span>
  );
}

/** Dropped, calmly: a dimmed cover and a small neutral disc carrying a `danger` ✕. */
function DroppedMark() {
  return (
    <span aria-hidden className="pointer-events-none absolute inset-0">
      <span className="absolute inset-0 bg-overlay" />
      <span className="absolute right-1.5 top-1.5 flex size-5 items-center justify-center rounded-full bg-surface text-danger">
        <X className="size-3" strokeWidth={2.25} />
      </span>
    </span>
  );
}

/** The viewer's own stars — the wall's one rating (`rating` colour). */
export function UserRating({ value }: { value: number }) {
  return (
    <span className="flex shrink-0 items-center gap-0.5 text-caption tabular-nums text-rating">
      <Star aria-hidden className="size-3 fill-current" strokeWidth={1.75} />
      {value}
    </span>
  );
}

/** The wall's loading shape: the same tracks, a cover and two caption lines per tile. */
export function LibraryGridSkeleton({ label }: { label: string }) {
  return (
    <div role="status" aria-label={label} className={SHELF_GRID}>
      {Array.from({ length: SETTLE_COVERS }, (_, i) => (
        <div key={i}>
          <Skeleton className="aspect-[2/3] w-full rounded-sm" />
          <Skeleton className="mt-2 h-3.5 w-3/4" />
          <Skeleton className="mt-1.5 h-3 w-1/3" />
        </div>
      ))}
    </div>
  );
}
